import React, {useEffect, useState} from "react";
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    List,
    ListItem as ListItemBase,
    ListItemIcon,
    ListItemText,
    Paper,
    Typography
} from "@mui/material";
import {Search} from "@mui/icons-material";
import { Alert } from '@mui/material';
import EnhancedErrorDialog from "../../../components/EnhancedErrorDialog";
import {utilCreateSupplierGroup, utilUpdateSupplierGroup} from "../../../utils/utils";
import EnhancedTextField from "../../../components/EnhancedTextField";
import EnhancedCircularLoader from "../../../components/EnhancedCircularLoader";

// Relax legacy `button` prop typing on ListItem to `any` (behavior unchanged).
const ListItem: any = ListItemBase;

interface SupplierGroupPartner {
    Id: number;
    CompanyName: string;
    [key: string]: any;
}

interface SupplierGroupConfig {
    Id?: number | string;
    Name?: string;
    PartnerIds?: (number | string)[];
    Partners?: (SupplierGroupPartner | number | string)[];
    [key: string]: any;
}

interface SupplierGroupDialogProps {
    open: boolean;
    setOpen: (open: boolean) => void;
    group?: SupplierGroupConfig | null;
    partners: SupplierGroupPartner[];
    onSave?: () => void;
}

export const SupplierGroupDialog = (props: SupplierGroupDialogProps) => {
    const {open, setOpen, group, partners, onSave} = props;
    const [loading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState({open: false, errorHeader: "", errorMessage: ""});
    const [groupName, setGroupName] = useState("");
    const [selectedPartners, setSelectedPartners] = useState<number[]>([]);
    const [searchTerm, setSearchTerm] = useState("");

    const isEdit = !!group;
    
    // Sort partners alphabetically for consistent display
    const sortedPartners = React.useMemo(() => {
        return [...partners].sort((a, b) => a.CompanyName.localeCompare(b.CompanyName));
    }, [partners]);

    useEffect(() => {
        if (open && group) {
            setGroupName(group.Name || "");
            if (group.PartnerIds && Array.isArray(group.PartnerIds)) {
                setSelectedPartners(group.PartnerIds.map(id => typeof id === 'string' ? parseInt(id) : id) as number[]);
            } else if (group.Partners && Array.isArray(group.Partners)) {
                const partnerIds: number[] = [];
                for (const p of group.Partners) {
                    if (typeof p === 'object' && p !== null && 'Id' in p) {
                        partnerIds.push(p.Id);
                    } else if (typeof p === 'number') {
                        partnerIds.push(p);
                    } else if (typeof p === 'string') {
                        const partner = sortedPartners.find(partner => partner.CompanyName === p);
                        if (partner) {
                            partnerIds.push(partner.Id);
                        }
                    }
                }
                setSelectedPartners(partnerIds);
            } else {
                setSelectedPartners([]);
            }
        } else if (open) {
            setGroupName("");
            setSelectedPartners([]);
        }
        setSearchTerm("");
    }, [open, group, sortedPartners]);

    const handleTogglePartner = (partnerId: number) => {
        const currentIndex = selectedPartners.indexOf(partnerId);
        const newSelected = [...selectedPartners];

        if (currentIndex === -1) {
            newSelected.push(partnerId);
        } else {
            newSelected.splice(currentIndex, 1);
        }

        setSelectedPartners(newSelected);
    };

    const handleToggleAll = () => {
        if (selectedPartners.length === sortedPartners.length) {
            setSelectedPartners([]);
        } else {
            setSelectedPartners(sortedPartners.map(p => p.Id));
        }
    };

    const filteredPartners = sortedPartners.filter(partner => 
        partner.CompanyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        partner.Id.toString().includes(searchTerm)
    );

    const submit = async () => {
        if (!groupName.trim()) {
            setError({open: true, errorHeader: "Validation Error", errorMessage: "Group name is required"});
            return;
        }

        if (groupName.includes(',')) {
            setError({open: true, errorHeader: "Validation Error", errorMessage: "Group names cannot contain commas"});
            return;
        }

        if (selectedPartners.length === 0) {
            setError({open: true, errorHeader: "Validation Error", errorMessage: "Please select at least one supplier"});
            return;
        }

        setSubmitting(true);

        try {
            let result;
            if (isEdit) {
                result = await utilUpdateSupplierGroup(group!.Id as string, groupName, selectedPartners);
            } else {
                result = await utilCreateSupplierGroup(groupName, selectedPartners);
            }

            if (result.errorMessage) {
                setError({open: true, errorHeader: "Error", errorMessage: result.errorMessage});
            } else {
                setOpen(false);
                if (onSave) onSave();
            }
        } catch (err: any) {
            setError({open: true, errorHeader: "Error", errorMessage: err.message});
        }

        setSubmitting(false);
    };

    return (
        <>
            <Dialog 
                open={open} 
                onClose={() => !submitting && setOpen(false)} 
                maxWidth={"md"} 
                fullWidth={true}
                slotProps={{
                    paper: {
                        style: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                            overflow: 'hidden'
                        }
                    }
                }}
            >
                {loading ? (
                    <Paper style={{borderRadius: '4px'}}>
                        <DialogTitle
                            style={{
                                padding: "16px",
                                paddingBottom: "0px"
                            }}
                        >
                            Supplier Group Information
                        </DialogTitle>
                        <DialogContent
                            style={{
                                padding: "16px",
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '400px'
                            }}
                        >
                            <EnhancedCircularLoader/>
                        </DialogContent>
                    </Paper>
                ) : (
                    <Paper style={{borderRadius: '4px'}}>
                        <DialogTitle
                            style={{
                                padding: "16px",
                                paddingBottom: "0px"
                            }}
                        >
                            {isEdit ? group!.Name : "Supplier Group Information"}

                            <Alert severity="info" style={{marginTop: "16px"}}>
                                Supplier groups help organize partners for easier filtering and management.
                            </Alert>
                        </DialogTitle>

                        <DialogContent
                            style={{
                                padding: "16px",
                                paddingTop: "0px"
                            }}
                        >
                            <Grid container style={{paddingTop: "16px"}}>
                                <Grid size={12}>
                                    <EnhancedTextField
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        label="Group Name"
                                        required
                                        error={(groupName.includes(',') || null) as any}
                                        helperText={groupName.includes(',') ? "Group names cannot contain commas" : ""}
                                        disabled={submitting}
                                    />
                                </Grid>

                                <Grid size={12} style={{paddingTop: "16px"}}>
                                    <Typography variant="body2" color="textSecondary" style={{marginBottom: "8px"}}>
                                        Select Suppliers
                                    </Typography>
                                    
                                    <Grid container alignItems="center" justifyContent="space-between" style={{marginBottom: "8px"}}>
                                        <Typography variant="caption" color="textSecondary">
                                            {selectedPartners.length} of {sortedPartners.length} suppliers selected
                                        </Typography>
                                        <Button
                                            variant="text"
                                            size="small"
                                            color="primary"
                                            onClick={handleToggleAll}
                                            style={{textTransform: 'none', padding: '2px 8px'}}
                                        >
                                            {selectedPartners.length === sortedPartners.length ? "Deselect All" : "Select All"}
                                        </Button>
                                    </Grid>

                                    <EnhancedTextField
                                        placeholder="Search suppliers..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{marginBottom: "8px"}}
                                        InputProps={{
                                            startAdornment: <Search style={{marginRight: 8, color: "#999", fontSize: 18}}/>
                                        }}
                                    />

                                    <Paper variant="outlined" style={{padding: 0}}>
                                        <List 
                                            style={{
                                                maxHeight: 300,
                                                overflow: "auto",
                                                padding: 0
                                            }}
                                        >
                                            {filteredPartners.map((partner) => (
                                                <ListItem 
                                                    key={partner.Id} 
                                                    button 
                                                    onClick={() => handleTogglePartner(partner.Id)}
                                                    style={{
                                                        paddingTop: 6,
                                                        paddingBottom: 6,
                                                        borderBottom: '1px solid #f0f0f0'
                                                    }}
                                                >
                                                    <ListItemIcon style={{minWidth: 40}}>
                                                        <Checkbox
                                                            edge="start"
                                                            checked={selectedPartners.indexOf(partner.Id) !== -1}
                                                            tabIndex={-1}
                                                            disableRipple
                                                            color="primary"
                                                            size="small"
                                                        />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={partner.CompanyName}
                                                        secondary={`ID: ${partner.Id}`}
                                                        slotProps={{
                                                            primary: {variant: 'body2'},
                                                            secondary: {variant: 'caption', color: 'textSecondary'}
                                                        }} />
                                                </ListItem>
                                            ))}
                                            {filteredPartners.length === 0 && (
                                                <ListItem>
                                                    <ListItemText 
                                                        primary="No suppliers found"
                                                        style={{textAlign: "center", color: "#999"}}
                                                        slotProps={{
                                                            primary: {variant: 'body2'}
                                                        }}
                                                    />
                                                </ListItem>
                                            )}
                                        </List>
                                    </Paper>
                                    
                                    {selectedPartners.length === 0 && (
                                        <Typography variant="caption" color="error" style={{marginTop: "4px", display: 'block'}}>
                                            Select at least one supplier for this group
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </DialogContent>

                        <DialogActions style={{padding: "16px", paddingTop: "8px"}}>
                            <Button
                                variant="text"
                                onClick={() => setOpen(false)}
                                disabled={submitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="text"
                                color="primary"
                                onClick={submit}
                                disabled={submitting || !groupName.trim() || selectedPartners.length === 0}
                            >
                                {submitting ? <CircularProgress size={20}/> : (isEdit ? "Update" : "Save")}
                            </Button>
                        </DialogActions>
                    </Paper>
                )}
            </Dialog>
            <EnhancedErrorDialog
                open={error.open}
                errorHeader={error.errorHeader}
                errorMessage={error.errorMessage}
                onClose={() => setError({open: false, errorHeader: "", errorMessage: ""})}
            />
        </>
    );
};